import {Box, ButtonBase, Checkbox, IconButton, Tooltip, Typography} from "@mui/material";
import {useState} from "react";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import QuantitySelector from "../../components/quantity-selector";
import AppliedDiscount from "../../components/common/applied-discount";
import {cartLineTotal} from "../../components/common/cart-pricing";
import {formatOrderMoney} from "../../components/orders/order-money";

export const wideCard = "@container (min-width: 760px)";
export const checkoutColumns = "minmax(0, 1fr) 88px 132px 112px 128px 32px";
const caption = {display: "block", color: "text.secondary", mb: 0.5, [wideCard]: {display: "none"}};

export default function CheckoutProductRow({row, selected, disabled, onSelect, onQuantityChange, onRemove, onDetails}) {
    const {t} = useTranslation();
    const [quantityRevision, setQuantityRevision] = useState(0);
    const changeQuantity = async quantity => {
        await onQuantityChange(row, quantity);
        // The shared selector owns a draft; restore the authoritative value even when a write was rejected.
        setQuantityRevision(previous => previous + 1);
    };
    return <Box component="li" data-cart-id={row.cartId} sx={{listStyle: "none", display: "grid", gap: 1.5, p: {xs: 1.5, sm: 2},
        gridTemplateColumns: "minmax(0, 1fr) 100px 28px", gridTemplateAreas: '"product product remove" "expiry price price" "quantity total total"',
        borderTop: "1px solid", borderColor: "divider", alignItems: "center", minWidth: 0,
        [wideCard]: {gridTemplateColumns: checkoutColumns, gridTemplateAreas: '"product expiry price quantity total remove"'}}}>
        <Box sx={{gridArea: "product", display: "flex", gap: 1, alignItems: "flex-start", minWidth: 0}}>
            <Checkbox size="small" checked={selected} disabled={disabled} onChange={onSelect}
                inputProps={{"aria-label": t("CheckoutSelectProduct", {name: row.productName || row.productCode})}} sx={{p: 0.5}}/>
            <Box sx={{flex: 1, minWidth: 0}}>
                <ButtonBase disabled={disabled} onClick={() => onDetails(row)} sx={{display: "block", textAlign: "left", width: "100%",
                    borderRadius: 0.5, "&:hover": {color: "primary.main"}, "&.Mui-focusVisible": {outline: "2px solid", outlineColor: "primary.main"}}}
                    aria-label={t("BasketViewProduct", {name: row.productName || row.productCode})}>
                    <Typography variant="body2" sx={{overflowWrap: "anywhere"}}>{row.productName || row.productCode}</Typography>
                </ButtonBase>
                <Typography variant="caption" color="text.secondary">{row.productCode}</Typography>
            </Box>
        </Box>
        <Box sx={{gridArea: "expiry", minWidth: 0}}>
            <Typography variant="caption" sx={caption}>{t("ExpirationDate")}</Typography>
            <Typography variant="body2">{row.endDate && dayjs(row.endDate).isValid() ? dayjs(row.endDate).format("MM/YYYY") : "—"}</Typography>
        </Box>
        <Box sx={{gridArea: "price", minWidth: 0}}><Typography variant="caption" sx={caption}>{t("UnitPrice")}</Typography><AppliedDiscount product={row}/></Box>
        <Box component="fieldset" disabled={disabled} sx={{gridArea: "quantity", border: 0, p: 0, m: 0, minWidth: 0}}>
            <Typography component="legend" variant="caption" sx={caption}>{t("Quantity")}</Typography>
            <QuantitySelector key={quantityRevision} initialValue={row.cartQuantity} min={1} max={999} showActionTooltips
                onChange={changeQuantity} inputLabel={t("BasketProductQuantity", {name: row.productName})}
                decreaseLabel={t("BasketDecreaseQuantity", {name: row.productName})} increaseLabel={t("BasketIncreaseQuantity", {name: row.productName})}/>
        </Box>
        <Box sx={{gridArea: "total", minWidth: 0, textAlign: "right"}}><Typography variant="caption" sx={caption}>{t("Total")}</Typography>
            <Typography variant="body2" fontWeight={600}>{formatOrderMoney(cartLineTotal(row, row.cartQuantity), row.currency)}</Typography></Box>
        <Box data-checkout-row-action="remove" sx={{gridArea: "remove", justifySelf: "end", alignSelf: "start",
            [wideCard]: {justifySelf: "center", alignSelf: "center"}}}>
            <Tooltip title={t("BasketRemoveProduct", {name: row.productName || row.productCode})} arrow>
                <span><IconButton size="small" disabled={disabled} onClick={() => onRemove(row.cartId)}
                    aria-label={t("BasketRemoveProduct", {name: row.productName || row.productCode})}><ClearOutlinedIcon fontSize="small"/></IconButton></span>
            </Tooltip>
        </Box>
    </Box>;
}
